import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {AuthService} from '../../../../../firebase/src/lib/services/auth.service';
import {map} from 'rxjs';
import {ChartData} from '../../../../../shared/src/lib/components/mixed-chart/mixed-chart.component';

export enum IntervalName {
    year = 'year',
    month = 'month',
    quartal = 'quartal'
}

export enum ChartType {
    anxiety = 'anxiety',
    depression = 'depression',
    performance = 'performance'
}

export interface YAxis {
    y0: number;
    y1: number;
    label?: string;
}

export interface XAxis {
    x0: number;
    x1: number;
    label?: string;
}

export interface Datum2 {
    id: string;
    xAxis: XAxis;
    yAxis: YAxis;
    labels?: any;
    segments?: any;
    data: Datum[];
    histogram: Datum[];
}

export interface Datum {
    x: number;
    y: number;
}

export interface Depression {
    id: string;
    xAxis: XAxis;
    yAxis: YAxis;
    labels: any[];
    segments: number[][];
    data: any[];
}

export interface Anxiety {
    id: string;
    xAxis: XAxis;
    yAxis: YAxis;
    labels: string[];
    segments: number[][];
    data: any[];
}

export interface Performance {
    title?: any;
    subtitle?: any;
    score?: any;
    data: Datum2[];
}

export interface Burnout {
    title: string;
}

export interface DashboardRangeData {
    performance: Performance;
    anxiety: Anxiety;
    depression: Depression;
    burnout: Burnout;
    important_parameters: Burnout;
    details: Burnout;
    recommendations: Burnout;
}

export interface DashboardData {
    date: Date;
    month: DashboardRangeData;
}

@Injectable({
    providedIn: 'root'
})
export class ReportService {

    reportData: any = null;

    constructor(private http: HttpClient,
                private auth: AuthService,
    ) {

    }

    get report() {
        return this.reportData;
    }

    set report(data: any) {
        this.reportData = data;
    }

    refreshDashboard(dashboardId: any) {
        const headers = new HttpHeaders({
            Authorization: `Bearer ${this.auth.userData?.stsTokenManager?.accessToken}`
        });
        return this.http.post(`${environment.apiUrl}/dashboard/${dashboardId}/refresh`, null, {
            headers
        });
    }

    fetchData(dashboardId: any) {
        const headers = new HttpHeaders({
            Authorization: `Bearer ${this.auth.userData?.stsTokenManager?.accessToken}`
        });
        return this.http.get(`${environment.apiUrl}/dashboard/${dashboardId}`, {
            headers
        })
            .pipe(
                map(res => {
                    this.report = res;
                    return res;
                })
            );
    }

    fetchReport() {
        const headers = new HttpHeaders({
            Authorization: `Bearer ${this.auth.userData?.stsTokenManager?.accessToken}`
        });
        return this.http.get(`${environment.apiUrl}/report`, {
            headers
        })
            .pipe(
                map(res => {
                    this.report = res;
                    return res;
                })
            );
    }

    getDataByInterval(data: any, intervalName: IntervalName) {
        return data[intervalName];
    }

    dashboardData(data: any, type: ChartType, interval: IntervalName) {
        return this.getDataByInterval(data, interval)[type];
    }

    dataByInterval(data: any, type: string, interval: IntervalName) {
        return this.getDataByInterval(data, interval)[type];
    }

    mapReactionsData(data: any, group) {
        const values = data.find((el: any) => el.id === group);
        return Object.entries(values.data).map((item: any) => {
            const defaultValues: any = data.find((el: any) => el.id === 'Ukraine').data[item[0]];
            return {
                id: item[0],
                labels: item[0] === 'hypervigilance' ? ['Low', 'Optimal', 'High'] : ['Minimal', 'Moderate', 'Severe'],
                colors: item[0] === 'hypervigilance' ? ['#fff649', '#0fff7e', '#fe5852'] : ['#0fff7e', '#fff649', '#fe5852'],
                datasets: [
                    {data:  item[1], label: group.toUpperCase()},
                    {data: defaultValues, label: 'Ukraine'},
                ],
            };
        });
    }

    mapPerformanceData(data: any): { xAxis: XAxis, yAxis: YAxis, data: ChartData[] } {
        const dashboardData = data;
        if (!dashboardData) {
            return;
        }
        const {xAxis, yAxis} = dashboardData.data.find((el => el.id === 'Ukraine'));
        const barArray = dashboardData.data.find((el => el.id === 'Ukraine'))
            .histogram.map((el) => {
                return {
                    x: el.x,
                    yBar: el.y
                };
            });
        const greyArray = dashboardData.data.find((el => el.id === 'Ukraine'))
            .data.map((el) => {
                return {
                    x: el.x,
                    yGreyLine: el.y
                };
            });
        const colorArray = dashboardData.data.find((el => el.id !== 'Ukraine'))
            .data.map((el) => {
                return {
                    x: el.x,
                    yColorLine: el.y
                };
            });

        const dataMap: any = new Map();
        barArray.forEach(item => dataMap.set(item.x, item));
        colorArray.forEach(item => dataMap.set(item.x, {...dataMap.get(item.x), ...item}));
        greyArray.forEach(item => dataMap.set(item.x, {...dataMap.get(item.x), ...item}));
        const mergedArr: ChartData[] = Array.from(dataMap.values());
        return {
            data: mergedArr,
            xAxis,
            yAxis
        };
    }

    mapBarValues(data: any) {
        return data
            .map((el: any) => {
                return {
                    barTitle: el.text,
                    values: Object.values(el.data)
                };
            });
    }
}
